import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import { useEffect, useMemo, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { useSubmitNoteFeedback } from 'src/hooks/api/useSubmitNoteFeedback';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { AddFileIcon } from 'src/svgs/AddFileIcon';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Feature,
  Permission,
  ReachProSupportCaseAttachmentResponse,
  ReachProSupportCaseClient,
} from 'src/WebApiController';

import * as styles from './ReachProSupportCaseMessage.css';

export interface CaseMessage {
  id: string;
  content: string;
  senderName: string;
  timestamp: Date;
  isSent: boolean;
}

interface ReachProSupportCaseMessageProps {
  message: CaseMessage;
  workItemId?: number;
  attachments?: ReachProSupportCaseAttachmentResponse[];
}

interface AttachmentItemProps {
  workItemId: number;
  attachment: ReachProSupportCaseAttachmentResponse;
}

function AttachmentItem({ workItemId, attachment }: AttachmentItemProps) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const hasReachProSupportFeature = useUserHasFeature(Feature.ReachProSupport);
  const hasSupportViewPermission = useUserHasAnyOfPermissions(
    Permission.SellerSupportCase_View
  );
  const shouldQuery = hasReachProSupportFeature && hasSupportViewPermission;

  const { data: objectUrl, isLoading } = useQuery({
    queryKey: [
      'reachpro-support-case-attachment',
      workItemId,
      attachment.documentId,
    ],
    queryFn: async () => {
      if (!shouldQuery) {
        return null;
      }
      const response = await tryInvokeApi(
        async () => {
          return await new ReachProSupportCaseClient(
            activeAccountWebClientConfig
          ).getSupportCaseAttachment(workItemId, attachment.documentId);
        },
        (error) => {
          trackError(
            'ReachProSupportCaseClient.getSupportCaseAttachment',
            error
          );
        }
      );
      if (!response?.data) {
        return null;
      }
      return URL.createObjectURL(response.data);
    },
    enabled: shouldQuery,
    // Object URLs should not be stale-reused across mounts; re-fetch each time
    staleTime: 0,
    gcTime: 0,
  });

  // Revoke the object URL when the component unmounts or when the URL changes
  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  if (isLoading) {
    return (
      <div className={styles.attachmentPlaceholder}>
        <PosSpinner size={vars.iconSize.m} />
      </div>
    );
  }

  if (!objectUrl) {
    return null;
  }

  if (attachment.isImage) {
    return (
      <a
        href={objectUrl}
        download={attachment.fileName}
        className={styles.thumbnailAnchor}
        title={attachment.fileName}
      >
        <img
          src={objectUrl}
          alt={attachment.fileName}
          className={styles.thumbnailImage}
        />
      </a>
    );
  }

  return (
    <a
      href={objectUrl}
      download={attachment.fileName}
      className={styles.fileChip}
      title={attachment.fileName}
    >
      <AddFileIcon size={vars.iconSize.m} />
      <span className={styles.fileChipName}>{attachment.fileName}</span>
    </a>
  );
}

type Rating = 'up' | 'down' | null;

// IM survey feedback content IDs (survey 113)
const FEEDBACK_QUESTION_CONTENT_ID = 1568683;
const FEEDBACK_ANSWER_POSITIVE_CONTENT_ID = '1568684';
const FEEDBACK_ANSWER_NEGATIVE_CONTENT_ID = '1568691';

export function ReachProSupportCaseMessage({
  message,
  workItemId,
  attachments,
}: ReachProSupportCaseMessageProps) {
  const [rating, setRating] = useState<Rating>(null);
  const submitFeedback = useSubmitNoteFeedback();

  const sanitizedContent = useMemo(() => {
    return DOMPurify.sanitize(message.content, {
      ALLOWED_TAGS: [
        'br',
        'div',
        'p',
        'span',
        'b',
        'i',
        'strong',
        'em',
        'ul',
        'ol',
        'li',
        'a',
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
    });
  }, [message.content]);

  const hasAttachments =
    workItemId != null && attachments != null && attachments.length > 0;

  const handleRating = (value: 'up' | 'down') => {
    setRating((prev) => (prev === value ? null : value));

    const noteId = parseInt(message.id);
    if (!isNaN(noteId)) {
      const answerContentId =
        value === 'up'
          ? FEEDBACK_ANSWER_POSITIVE_CONTENT_ID
          : FEEDBACK_ANSWER_NEGATIVE_CONTENT_ID;

      submitFeedback.mutate({
        noteId,
        answerContentIds: answerContentId,
        answerText: value === 'up' ? 'Helpful' : 'Not helpful',
        questionContentId: FEEDBACK_QUESTION_CONTENT_ID,
      });
    }
  };

  return (
    <div
      className={`${styles.messageBubble} ${
        message.isSent ? styles.messageBubbleSent : styles.messageBubbleReceived
      }`}
    >
      <div className={styles.messageHeader}>
        <span
          className={clsx(
            styles.senderAvatar,
            message.isSent
              ? styles.senderAvatarSent
              : styles.senderAvatarReceived
          )}
        >
          {message.senderName?.charAt(0)?.toUpperCase() ?? '?'}
        </span>
        {message.senderName}
        <span className={styles.messageTimestamp}>
          {' • '}
          {message.timestamp.toLocaleString([], {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </span>
      </div>
      <div
        className={styles.messageContent}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
      {hasAttachments && (
        <div className={styles.attachmentsRow}>
          {attachments!.map((attachment) => (
            <AttachmentItem
              key={attachment.documentId}
              workItemId={workItemId!}
              attachment={attachment}
            />
          ))}
        </div>
      )}
      {!message.isSent && (
        <div className={styles.ratingRow}>
          <span className={styles.ratingLabel}>Was this helpful?</span>
          <button
            type="button"
            className={clsx(
              styles.ratingButton,
              rating === 'up' && styles.ratingButtonActive
            )}
            onClick={() => handleRating('up')}
            aria-label="Helpful"
          >
            👍
          </button>
          <button
            type="button"
            className={clsx(
              styles.ratingButton,
              rating === 'down' && styles.ratingButtonActive
            )}
            onClick={() => handleRating('down')}
            aria-label="Not helpful"
          >
            👎
          </button>
        </div>
      )}
    </div>
  );
}
